import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Divider, Paper, Typography } from '@mui/material'
import { Dropdown, DropdownButton } from 'react-bootstrap'
import { IoDownloadOutline , IoOpenOutline } from "react-icons/io5"
import { MdAutorenew } from "react-icons/md"

import EquipmentForm from './Forms/EquipmentForm'
import CertificateRenewForm from './Forms/CertificateRenewForm'
import CertificateUploadForm from './Forms/CertificateUploadForm'

import DataTable from 'Shared/Components/DataTable'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import PopupModal from 'Shared/Components/PopupModal'
import BackLinkButton from 'Shared/Components/BackLinkButton'
import { saveAs } from 'file-saver'

const AssetsListDetail = () => {
  const { profile, role, setPageLoading } = useOutletContext()

  const { t } = useTranslation()
  const { assets_id } = useParams()
  const { send } = useFetchData()
  const navigate = useNavigate()

  const [data, setData] = useState()
  const [isEquipmentFormOpen, setEquipmentFormOpen] = useState(false)
  const [isCertificateRenewFormOpen, setCertificateRenewFormOpen] = useState()
  const [isCertificateUploadFormOpen, setCertificateUploadFormOpen] = useState(false)

  const getEquipments = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/assets_tracking/detail`,
      params: {
        "assets_id": assets_id
      },
      returnType: "json"
    })

    body && setData(body)
  }

  const loadData = async () => {
    await Promise.all([
      getEquipments()
    ])
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await loadData()

      setPageLoading(false)
    }

    init()
  }, [])

  const reload = async () => {
    await loadData()
  }

  const download = async (row, mode) => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/assets_tracking/certificate_download`,
      params: {
        "certificate_version_id": row["certificate_version_id"]
      },
      returnType: "blob"
    })

    if (blob != null) {
      if (mode === "download") {
        saveAs(blob, `${row["certificate_version_id"]}.pdf`)
      }
      else if (mode === "view") {
        const url = URL.createObjectURL(blob)
        window.open(url)
      }
    }

    setPageLoading(false)
  }

  const updateDeviceStatus = async (new_status) => {
    setPageLoading(true)

    const res = await send({
      method: "PUT",
      url: `${apiUrl}/assets_tracking/equipment/${assets_id}/update_status`,
      contentType: "json",
      body: {
        "status": new_status
      },
      returnType: "json"
    })

    if (res != null) {
      if (new_status != -1) {
        await reload()
      }
      else {
        navigate("./..")
      }
    }

    setPageLoading(false)
  }

  return (<>
    <div className="mainContainer scrollbar" style={{ borderRadius: "15px", backgroundColor: "white", overflow: "auto" }}>
      {data && <>
        <BackLinkButton />

        <div style={{ flexGrow: 1, overflow: "auto", margin: "10px", paddingLeft: "10px", paddingRight: "10px" }}>
          <div style={{ display: "flex", justifyContent: "end", marginRight: "15px" }}>
            <DropdownButton title={t("general_action")}>
              <Dropdown.Item onClick={() => setEquipmentFormOpen(true)}>{t("general_edit")}</Dropdown.Item>
              <Dropdown.Item onClick={() => updateDeviceStatus(data["detail"]["status"] === 1 ? 0 : 1)}>{data["detail"]["status"] === 1 ? t("equipment_deactivateEquipment") : t("equipment_activateEquipment")}</Dropdown.Item>
              <Dropdown.Item onClick={() => updateDeviceStatus(-1)}>{t("equipment_removeEquipment")}</Dropdown.Item>
              <Dropdown.Item onClick={() => setCertificateUploadFormOpen(true)}>{t("equipment_uploadCertificate")}</Dropdown.Item>
            </DropdownButton>
          </div>
        
          <div>
            <div>
              <Typography variant="h6"><b>{t("assetsTracking_equipmentDetail")}</b></Typography>

              <div>
                <div>{t("assetsTracking_equipmentName")}: {data["detail"]["equipment_name"] ?? "-"}</div>
                <div>{t("assetsTracking_equipmentDescription")}: {data["detail"]["description"] ?? "-"}</div>
                <div>{t("assetsTracking_equipmentType")}: {data["detail"]["type_name"] ?? "-"}</div>
                <div>{t("assetsTracking_manufacturer")}: {data["detail"]["manufacturer"] ?? "-"}</div>
                <div>{t("assetsTracking_model")}: {data["detail"]["model"] ?? "-"}</div>
                <div>{t("assetsTracking_remark")}: {data["detail"]["remark"] ?? "-"}</div>
                <div>{t("assetsTracking_status")}: {data["detail"]["status"] === 0 ? t("assetsTracking_equipmentInactive") : t("assetsTracking_active")}</div>
            </div>
            </div>

            <Divider sx={{ bgcolor: "secondary.light", marginTop: "30px", marginBottom: "30px" }}/>    

            <div>
              <Typography variant="h6"><b>{t("assetsTracking_equipmentCertificates")}</b></Typography>

              {data["certificates"].length > 0 ? (
                <Paper elevation={0} sx={{ height: "100%", display: "flex", flexFlow: "column" }}>
                  <DataTable
                    data={data["certificates"]}
                    columns={{
                      "certificate_name": { "label": t("assetsTracking_certificateName"), "sorting": false, "nowrap": true },
                      "due_date": { "label": t("assetsTracking_dueDate"), "sorting": false, "nowrap": true }
                    }}
                    renderActions={(row) => { return [
                      ["pm", "admin"].includes(role) && {icon: <MdAutorenew size={20}/>, text: t("equipment_renewCertificate"), onClick: function(row) { setCertificateRenewFormOpen(row["certificate_id"]) }},
                    ]}}
                  />
                </Paper>
              ) : (
                <div style={{ width: "100%", textAlign: "center" }}>
                  <b>No Record</b>
                </div>
              )}
            </div>

            <Divider sx={{ bgcolor: "secondary.light", marginTop: "30px", marginBottom: "30px" }}/>    

            <div>
              <Typography variant="h6"><b>{t("assetsTracking_equimentPastActivities")}</b></Typography>

              {data["past_activities"].length > 0 ? (
                <Paper elevation={0} sx={{ height: "100%", display: "flex", flexFlow: "column" }}>
                  <DataTable
                    data={data["past_activities"]}
                    columns={{
                      "certificate_name": { "label": t("assetsTracking_certificateName"), "sorting": false, "nowrap": true },
                      "start_date": { "label": t("assetsTracking_certificateStartDate"), "sorting": false, "nowrap": true },
                      "end_date": { "label": t("assetsTracking_certificateEndDate"), "sorting": false, "nowrap": true }
                    }}
                    renderActions={(row) => { return [
                      {icon: <IoOpenOutline size={20}/>, text: t("general_view"), onClick: function(row) { download(row, "view") }},
                      ["pm", "so", "admin"].includes(role) && {icon: <IoDownloadOutline size={20}/>, text: t("general_download"), onClick: function(row) { download(row, "download") }}
                    ]}}
                  />
                </Paper>
              ) : (
                <div style={{ width: "100%", textAlign: "center" }}>
                  <b>No Record</b>
                </div>
              )}
              
            </div>
          </div>
        </div>

        <PopupModal isOpen={isEquipmentFormOpen} close toggle={() => setEquipmentFormOpen(false)} width="80%" height="80%">
          <EquipmentForm
            mode="edit" assets_id={assets_id} reload={reload}
            initialState={{
              "equipment_name": data["detail"]["equipment_name"],
              "equipment_description": data["detail"]["description"],
              "equipment_type": data["detail"]["type_id"],
              "manufacturer": data["detail"]["manufacturer"],
              "model": data["detail"]["model"],
              "remark": data["detail"]["remark"]
            }}
          />
        </PopupModal>

        <PopupModal isOpen={isCertificateRenewFormOpen != null} close toggle={() => setCertificateRenewFormOpen()} width="80%" height="80%">
          <CertificateRenewForm
            assets_id={assets_id}
            certificate_id={isCertificateRenewFormOpen}
            certificates={data["certificates"]}
            reload={reload}
          />
        </PopupModal>

        <PopupModal isOpen={isCertificateUploadFormOpen} close toggle={() => setCertificateUploadFormOpen(false)} width="80%" height="80%">
          <CertificateUploadForm
            assets_id={assets_id}
            reload={reload}
          />
        </PopupModal>
      </>}
    </div>
  </>)
}

export default AssetsListDetail
